/**
 * Wizard Layout Component
 *
 * Main layout wrapper with progress indicator and content area.
 *
 * Session 4.1: Wizard State Machine + Steps 1-2
 */

import type { ReactNode } from 'react';
import { WizardProgress } from './WizardProgress';
import { useWizard } from '../context/WizardContext';

interface WizardLayoutProps {
  children: ReactNode;
}

/**
 * Main wizard layout with header, progress, and content.
 */
export function WizardLayout({ children }: WizardLayoutProps) {
  const { state } = useWizard();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">
            Convert Payroll to C10
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Follow the steps to generate your NIB C10 file
          </p>
        </div>

        {/* Progress indicator */}
        <WizardProgress />

        {/* Error display */}
        {state.error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{state.error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Loading overlay */}
        {state.isLoading && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 flex items-center gap-4">
              <svg
                className="animate-spin h-6 w-6 text-blue-600"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-gray-700">Processing...</span>
            </div>
          </div>
        )}

        {/* Step content */}
        <div className="bg-white shadow rounded-lg p-6">{children}</div>
      </div>
    </div>
  );
}
